import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, Award, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import AccessRestricted from '../../components/AccessRestricted';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import { AssignmentRow, Course } from '../../types/learning.types';
import {
    assignmentSourceLabel,
    courseContentTypeLabel,
    durationLabel,
    learnerDueLabel,
} from '../../utils/learning';
import { cn } from '../../components/ui/utils';

/**
 * Course intro (LEARNING.md §7): what a learner sees before the player.
 *
 * The assignment row carries the learner-facing state (due date, source, progress); the
 * library course carries the catalogue detail (description, type, duration). If the
 * course has since been hidden from the library we fall back to what the row still knows.
 */
export default function CourseIntroPage() {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const { courseId: courseIdParam } = useParams<{ courseId: string }>();

    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_ENROLLMENT);
    const courseId = Number(courseIdParam);

    const [row, setRow] = useState<AssignmentRow | null>(null);
    const [course, setCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [my, library] = await Promise.all([
                learningApi.learner.my(),
                learningApi.learner.library(),
            ]);
            setRow([...my.assigned, ...my.self].find((r) => r.courseId === courseId) ?? null);
            setCourse(library.find((c) => c.id === courseId) ?? null);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load this course');
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        if (canRead && Number.isFinite(courseId)) load();
    }, [canRead, courseId, load]);

    const handleStart = async () => {
        // Start is idempotent server-side; a failure here (already in progress, network)
        // must not stop the learner from reaching the material.
        try {
            if (row) {
                await learningApi.learner.start(courseId);
            } else {
                await learningApi.learner.enroll(courseId);
            }
        } catch {
            // Ignore — the player is the real surface.
        }
        navigate(`/my-learning/${courseId}/player`);
    };

    if (!canRead) return <AccessRestricted />;
    if (loading) return <LoadingPageSkeleton />;

    const title = course?.title ?? row?.courseTitle ?? 'Course';
    const category = course?.category ?? row?.category ?? null;
    const issuesCertificate = course?.issuesCertificate ?? row?.issuesCertificate ?? false;

    const metaItems: string[] = [];
    if (course) {
        metaItems.push(courseContentTypeLabel(course.contentType));
        metaItems.push(
            course.passScorePercent ? `Pass mark ${course.passScorePercent}%` : 'No pass mark'
        );
    }
    if (row) metaItems.push(assignmentSourceLabel(row));

    const ctaLabel = !row
        ? 'Add to My Courses'
        : row.status === 'Completed'
            ? 'Review Course'
            : row.status === 'In Progress'
                ? 'Resume Learning'
                : 'Start Learning';

    return (
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-2 md:p-0">
            <Button
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => navigate('/my-learning')}
            >
                <ArrowLeft size={14} /> Back to My Learning
            </Button>

            {!row && !course ? (
                <div className="rounded-xl border border-dashed border-border p-14 text-center">
                    <div className="text-base font-semibold">This course isn't available to you right now</div>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        It may have been archived or hidden from the library. Head back to My Learning to see
                        what you can take.
                    </p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                    {/* ─── Tinted hero ─── */}
                    <div
                        className={cn(
                            'relative flex h-48 items-center justify-center',
                            !category?.colour && 'bg-muted/30'
                        )}
                        style={
                            category?.colour
                                ? { backgroundColor: `${category.colour}14` }
                                : undefined
                        }
                    >
                        <BookOpen
                            size={56}
                            className="opacity-85"
                            style={category?.colour ? { color: category.colour } : undefined}
                        />
                        {category && (
                            <span
                                className="absolute left-3.5 top-3.5 rounded-full border bg-white px-2 py-0.5 text-xs font-semibold"
                                style={{ color: category.colour, borderColor: category.colour }}
                            >
                                {category.name}
                            </span>
                        )}
                        {course && (
                            <span className="absolute bottom-3.5 right-3.5 rounded-full bg-white/90 px-2 py-0.5 text-xs text-muted-foreground">
                                {durationLabel(course.estimatedDurationMinutes)}
                            </span>
                        )}
                    </div>

                    {/* ─── Detail ─── */}
                    <div className="flex flex-col gap-3.5 p-6">
                        <h1 className="text-2xl font-semibold leading-tight">{title}</h1>

                        {metaItems.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                {metaItems.map((item, index) => (
                                    <React.Fragment key={item}>
                                        {index > 0 && <span>·</span>}
                                        <span>{item}</span>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {row && (
                            <div
                                className={cn(
                                    'text-sm font-medium',
                                    row.overdue ? 'text-destructive' : 'text-muted-foreground'
                                )}
                            >
                                {learnerDueLabel(row)}
                            </div>
                        )}

                        {course?.description && (
                            <p className="text-sm leading-relaxed text-foreground/80">
                                {course.description}
                            </p>
                        )}

                        {issuesCertificate && (
                            <div className="flex w-fit items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700">
                                <Award size={14} /> A certificate is issued on completion
                            </div>
                        )}

                        <Button
                            className="mt-1.5 h-10 w-fit min-w-[200px] rounded-lg px-6 text-sm font-semibold"
                            onClick={handleStart}
                        >
                            {ctaLabel}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
